import type { ReactNode } from "react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type { FirmwareVersion } from "~/api/firmware/types";
import { PersonalInformation } from "~/components/Patient/PatientPersonalInformation/components";
import { PaginatedTable } from "~/components/Shared/PaginatedTable";
import { DropdownMenu } from "~/ui";

interface Member {
  id: number;
  name: string;
  surname: string;
}

interface FirmwareTableProps {
  data: FirmwareVersion[];
  pageSize: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalRows: number;
  isLoading: boolean;
  isDownloadingFile?: boolean;
  selectedFileId?: number | null;
  onDownload?: (fileId: number) => void;
  onEdit?: (version: FirmwareVersion) => void;
  onDelete?: (version: FirmwareVersion) => void;
  sorting: SortingState;
  setSorting: (sorting: SortingState) => void;
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
}

export function FirmwareTable({
  data,
  pageSize,
  currentPage,
  setCurrentPage,
  totalRows,
  isLoading,
  isDownloadingFile,
  selectedFileId,
  onDownload,
  onEdit,
  onDelete,
  sorting,
  setSorting,
}: FirmwareTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleMemberClick = (memberId: number) => {
    navigate(`/airmen/${memberId}`);
  };

  const columns: ColumnDef<FirmwareVersion>[] = [
    {
      header: t("firmware.version", "Version"),
      accessorKey: "version",
    },
    {
      header: t("firmware.privacy", "Privacy"),
      accessorKey: "privacy_mode",
      enableSorting: false,
      cell: ({ row }: { row: { original: FirmwareVersion } }) => {
        const value = row.original.privacy_mode;
        const isPrivate = value === "PRIVATE";
        const colorClass = isPrivate
          ? "bg-red-700 text-white"
          : "bg-green-700 text-white";

        return (
          <span
            className={`rounded-full px-3 py-1 text-sm font-semibold ${colorClass}`}
            style={{ display: "inline-block", minWidth: 70 }}
          >
            {value}
          </span>
        );
      },
    },
    {
      header: t("firmware.members", "Members"),
      accessorKey: "private_member_ids",
      enableSorting: false,
      cell: ({ row }: { row: { original: FirmwareVersion } }): ReactNode => {
        const memberList = row.original.private_members as Member[];
        if (
          !Array.isArray(memberList) ||
          memberList.length === 0 ||
          row.original.privacy_mode === "PUBLIC"
        ) {
          return t("firmware.all_members", "All members");
        }
        const maxShow = 3;
        const showMembers = memberList.slice(0, maxShow);
        const hiddenMembers = memberList.slice(maxShow);
        return (
          <div className="flex items-center gap-1">
            {showMembers.map((m: Member) => (
              <button
                key={m.id}
                type="button"
                onClick={() => handleMemberClick(m.id)}
                className="cursor-pointer border-none bg-transparent p-0 transition-opacity hover:opacity-80"
                title={`${m.name} ${m.surname}`}
                aria-label={`View details for ${m.name} ${m.surname}`}
              >
                <PersonalInformation.Icon
                  icon={getInitials(`${m.name} ${m.surname}`)}
                  size="small"
                />
              </button>
            ))}
            {hiddenMembers.length > 0 && (
              <DropdownMenu
                type="ChevronDown"
                buttonAriaLabel={t(
                  "firmware.show_all_members",
                  "Show all members",
                )}
                options={hiddenMembers.map((m: Member) => ({
                  label: (
                    <div className="flex items-center gap-2">
                      <PersonalInformation.Icon
                        icon={getInitials(`${m.name} ${m.surname}`)}
                        size="small"
                      />
                      <span>{`${m.name} ${m.surname}`}</span>
                    </div>
                  ),
                  onClick: () => handleMemberClick(m.id),
                }))}
              />
            )}
          </div>
        );
      },
    },
    {
      header: t("firmware.uploaded_at", "Uploaded At"),
      accessorKey: "created_at",
      cell: ({ row }: { row: { original: FirmwareVersion } }) => {
        return new Date(row.original.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        });
      },
    },
    {
      id: "actions",
      cell: ({ row }: { row: { original: FirmwareVersion } }) => {
        const isCurrentFileDownloading =
          isDownloadingFile && selectedFileId === row.original.file.id;
        return (
          <div className="flex justify-end">
            <DropdownMenu
              buttonAriaLabel={t(
                "firmware.actions_for_version",
                "Actions for version {{version}}",
                { version: row.original.version },
              )}
              options={[
                {
                  label: t("general.edit"),
                  onClick: () => onEdit && onEdit(row.original),
                },
                {
                  label: isCurrentFileDownloading
                    ? t("firmware.downloading", "Downloading...")
                    : t("general.download"),
                  onClick: () =>
                    !isCurrentFileDownloading &&
                    onDownload &&
                    onDownload(row.original.file.id),
                },
                {
                  label: t("general.delete"),
                  onClick: () => onDelete && onDelete(row.original),
                },
              ]}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PaginatedTable
        data={data}
        columns={columns}
        pageSize={pageSize}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        sorting={sorting}
        setSorting={setSorting}
        totalRows={totalRows}
        isLoadingData={isLoading}
      />
    </>
  );
}
